/**
 * Indicator license evaluation — single source of truth for whether a given
 * indicator (in particular, a Premium Strategy such as Shadow Breaker) is
 * currently licensed for the signed-in user.
 *
 * Consumed by:
 * - `IndicatorPanel.tsx` — gates the Add Indicator dropdown (lock icon /
 *   disabled state) when browsing indicators.
 * - `ChartPanel.tsx` — revalidates indicators that are ALREADY on the chart.
 *   This runs on fresh login / relogin (metadata refetch), symbol change,
 *   timeframe change, and template load/apply, so an indicator whose license
 *   expires (or is revoked server-side) is pulled off the chart instead of
 *   continuing to run with stale, cached licensing state.
 *
 * Two independent expiry mechanisms exist in the `/metadata` response and
 * BOTH must pass for an indicator to be considered licensed:
 * 1. Generic per-indicator expiry — `indicatorsWithExpiry: {id, expiryDate}[]`
 *    keyed by the indicator's local engine ID (e.g. "shadow_breaker").
 * 2. Strategy license expiry — `indicatorExpiryDtMap: {metadataId: expiryDate}`
 *    where `metadataId` is resolved by matching the indicator's display
 *    name/shortName against `indicatorList` (the backend's per-user strategy
 *    catalogue). Only applies to indicators in the "strategies" category,
 *    and is bypassed for demo-licensed accounts.
 */
import { IndicatorCategory } from "../types/indicator.types";
import { IndicatorListItem } from "../../store/metadata-slice";
import { isExpiryDayActive, parseExpiryDay } from "../../utils/license-expiry";

export interface IndicatorLicenseContext {
  licenseType: string;
  indicatorsWithExpiry: { id: string; expiryDate: string }[];
  indicatorList: Array<string | IndicatorListItem>;
  indicatorExpiryDtMap: Record<string, string>;
}

export interface IndicatorLicenseSubject {
  id: string;
  name: string;
  shortName: string;
  category: IndicatorCategory;
}

export interface IndicatorLicenseStatus {
  /** Overall verdict — false means the indicator must not run / be addable. */
  licensed: boolean;
  /** Generic per-id expiry (indicatorsWithExpiry) has lapsed. */
  expired: boolean;
  /** Strategy license (indicatorExpiryDtMap) is missing or has lapsed. */
  unlicensed: boolean;
}

export const normalizeIndicatorKey = (value?: string): string =>
  (value || "").trim().toLowerCase().replace(/[_\s-]+/g, " ");

export const isUnexpired = (expiryDate?: string): boolean => {
  if (!expiryDate) return false;
  // Day-granular: the backend sends a date only, and a license that expires
  // today remains active for the whole of today.
  const active = isExpiryDayActive(expiryDate);
  // Do not lock paid indicators on malformed date strings from backend.
  return active === null ? true : active;
};

/** Resolve the active (non-expired) strategy-license ID set from indicatorExpiryDtMap. */
const activeLicensedStrategyIds = (indicatorExpiryDtMap: Record<string, string>): Set<string> =>
  new Set(
    Object.entries(indicatorExpiryDtMap || {})
      .filter(([, expiryDate]) => isUnexpired(expiryDate))
      .map(([id]) => id)
  );

const metadataStrategyEntries = (indicatorList: Array<string | IndicatorListItem>) =>
  indicatorList
    .map((item) => {
      if (typeof item === "string") {
        return { id: "", keys: [normalizeIndicatorKey(item)].filter(Boolean) };
      }
      return {
        id: item?.id || "",
        keys: [normalizeIndicatorKey(item?.id), normalizeIndicatorKey(item?.name)].filter(Boolean),
      };
    })
    .filter((entry) => entry.keys.length > 0);

// Match only by display name / shortName — never by local ID. Local IDs
// (e.g. "alpha_trade") normalise to strings identical to metadata names
// ("alpha trade" = "Alpha Trade") and cause false matches.
const resolveMetadataId = (
  indicator: IndicatorLicenseSubject,
  indicatorList: Array<string | IndicatorListItem>
): string => {
  const indicatorKeys = [
    normalizeIndicatorKey(indicator.name),
    normalizeIndicatorKey(indicator.shortName),
  ].filter(Boolean);

  const entries = metadataStrategyEntries(indicatorList);
  const matched = entries.find((entry) => entry.keys.some((key) => indicatorKeys.includes(key)));
  return matched?.id || "";
};

/** Evaluate the full license status of one indicator against the current metadata context. */
export function getIndicatorLicenseStatus(
  indicator: IndicatorLicenseSubject,
  ctx: IndicatorLicenseContext
): IndicatorLicenseStatus {
  const expiryMap = Object.fromEntries(
    (ctx.indicatorsWithExpiry || []).map((e) => [e.id, e.expiryDate])
  );
  const rawExpiry = expiryMap[indicator.id];
  const expired = !!rawExpiry && !isUnexpired(rawExpiry);

  if (indicator.category !== "strategies") {
    return { licensed: !expired, expired, unlicensed: false };
  }

  const isDemoLicense = ctx.licenseType?.toString().toLowerCase() === "demo";
  if (isDemoLicense) {
    return { licensed: !expired, expired, unlicensed: false };
  }

  const metadataId = resolveMetadataId(indicator, ctx.indicatorList || []);

  const activeIds = activeLicensedStrategyIds(ctx.indicatorExpiryDtMap || {});
  const normalizedActiveIds = new Set(Array.from(activeIds).map((id) => normalizeIndicatorKey(id)));

  const hasActiveStrategyLicense =
    !!metadataId &&
    (activeIds.has(metadataId) || normalizedActiveIds.has(normalizeIndicatorKey(metadataId)));

  const unlicensed = !hasActiveStrategyLicense;

  return { licensed: !expired && !unlicensed, expired, unlicensed };
}

export function isIndicatorLicensed(
  indicator: IndicatorLicenseSubject,
  ctx: IndicatorLicenseContext
): boolean {
  return getIndicatorLicenseStatus(indicator, ctx).licensed;
}

export interface IndicatorExpiryInfo {
  /** Raw expiry value as returned by `/metadata` (indicatorExpiryDtMap) — a date, e.g. "2026-10-05". */
  expiryDate: string;
  /** True once the expiry day has passed (a same-day license is still active). */
  expired: boolean;
}

/**
 * Resolve the raw strategy-license expiry for display purposes, e.g.
 * "Alpha Trade (valid: 2026-10-05)" in the Add Indicator list.
 * Returns null when the indicator isn't a Premium Strategy, or `/metadata`
 * has no matching license entry for it (e.g. demo accounts).
 */
export function getIndicatorExpiryInfo(
  indicator: IndicatorLicenseSubject,
  ctx: IndicatorLicenseContext
): IndicatorExpiryInfo | null {
  if (indicator.category !== "strategies") return null;

  const metadataId = resolveMetadataId(indicator, ctx.indicatorList || []);
  if (!metadataId) return null;

  const expiryDtMap = ctx.indicatorExpiryDtMap || {};
  const expiryDate =
    expiryDtMap[metadataId] ??
    Object.entries(expiryDtMap).find(
      ([id]) => normalizeIndicatorKey(id) === normalizeIndicatorKey(metadataId)
    )?.[1];

  if (!expiryDate) return null;

  return { expiryDate, expired: !isUnexpired(expiryDate) };
}

/** Format a raw expiry value as "YYYY-MM-DD" for UI display. */
export function formatExpiryForDisplay(expiryDate: string): string {
  const day = parseExpiryDay(expiryDate);
  if (!day) return expiryDate;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}
